import React, {useState} from 'react';
import SelectPicker from '../SelectPicker/SelectPicker';
import { contracts } from '../../ducks';
import registeredComponent from '../../utils/registeredComponent';
import duckify, { currentCustomer, singleDuck } from '../../utils/duckify';
import Placeholder from '../Placeholder';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {formatCurrency, parseCurrency} from "utils/i18n";
import { DISCOUNT_TYPES } from './ChargeTypes';

// Converts the discounts/aid fees to negative for the UI
const convertDiscount = (amount, contractFee) => (DISCOUNT_TYPES.find(x => x == contractFee.charge_type) ? -1 * amount : amount)

const getTotalAmountDefault = ({contract, contractFee, amountProperty}) => {
  if(amountProperty && contract){
    return parseCurrency(contract[amountProperty]) || 0;
  }

  if(contractFee) {
    return parseCurrency(convertDiscount(contractFee.amount, contractFee)) || 0;
  }

  return 0;
};

const getSplitAmountDefault =  ({contract, contractFee, amountProperty, currentSplit}) => {
  if (!currentSplit) {
    return 0;
  }

  if (amountProperty) {
    return parseCurrency(currentSplit[amountProperty]) || 0;
  }

  if (contractFee) {
    return parseCurrency(convertDiscount(currentSplit.charge_splits[contractFee.id], contractFee)) || 0;
  }

  return 0;
};

/**
 * Shows a fee list item in a contract totals field.
 *
 * If split contracts are enabled, this shows two columns, one for the current split first, then one for the total amount.
 *
 * @param contract                The contract being viewed.  Provided by duckify via the `contract_id` property.
 * @param selectedBillingPayerId  The current billing payer.  Provided by redux via the `contracts` reducer.
 * @param contractFee             Optional ContractFee we're showing the amounts for.  Either specify this or specify amountProperty or specify getTotalAmount and getSplitAmount.
 * @param amountProperty          Optional property to pull the amount from (i.e. 'deposit_amount' to show contract.deposit_amount)
 * @param allDucksReady           Provided by duckify.  Tells us when all ducks are loaded.
 * @param duckMeta                Provided by duckify.  Tells us the current state of each duck.
 * @param getTotalAmount          Optional function to get the total amount more specifically.  i.e. ({contract}) => contract.tuition_and_fees_total - contract.deposit_amount.  If you're setting this, you should also set getSplitAmount.
 * @param getSplitAmount          Optional function to get the amount for the current split (if split contracts are enabled).  If you're setting this, you should also set getTotalAmount.
 * @param className               Optional extra className to pass in.
 * @returns {JSX.Element}
 * @constructor
 */
const FeeListItemAmount = ({
  contract,
  selectedBillingPayerId,
  contractFee,
  amountProperty,
  allDucksReady,
  duckMeta,
  getTotalAmount = getTotalAmountDefault,
  getSplitAmount = getSplitAmountDefault,
  className = ''
}) => {
  const AmountPlaceholder = ({ amount }) => <Placeholder.text ready={allDucksReady && !duckMeta.contract.writing} lines={1} width={160}>
    <span className={'amount'}>{formatCurrency(amount)}</span>
  </Placeholder.text>;

  const currentSplit = contracts.hooks.useBillingPayerSplit(contract, selectedBillingPayerId);

  const splitAmount = contract.has_multiple_splits && currentSplit && getSplitAmount({contract, contractFee, amountProperty, currentSplit}) || 0;

  const totalAmount = getTotalAmount({contract, contractFee, amountProperty, currentSplit}) || 0;

  return <React.Fragment>
    {contract.has_multiple_splits && currentSplit && (
      <div className={classNames('fee-list-item-amount fee-list-item-amount-payer-split', {'fee-list-item-amount-negative': splitAmount < 0}, className)}>
        <AmountPlaceholder amount={splitAmount}/>
      </div>
    )}

    <div
      className={classNames('fee-list-item-amount', { 'fee-list-item-amount-negative': totalAmount < 0 }, className)}>
      <AmountPlaceholder amount={totalAmount}/>
    </div>

  </React.Fragment>;
};

const mapDucksToProps = ({contract_id}) => ({
  contract: singleDuck(contracts, contract_id)
});

const mapStateToProps = (state, props) => ({
  selectedBillingPayerId: state.contracts.currentContract.selectedBillingPayerId
});

const mapDispatchToProps = (dispatch, props) => ({
  selectBillingPayer: (billingPayerId) => dispatch(contracts.actions.selectBillingPayer(billingPayerId))
});

FeeListItemAmount.propTypes = {
  contract_id: PropTypes.string.isRequired, // ID of the contract we're currently looking at
  contractFee: PropTypes.object, // Contract fee to pull this amount from.  Either this or amountProperty is required
  amountProperty: PropTypes.string, // Name of a property to pull amount from.  Must be same name on both contract and contractSplit
  getTotalAmount: PropTypes.func, // Function to get total amount.  If you're overloading this you probably also want to overload getSplitAmount
  getSplitAmount: PropTypes.func // Function to get current split amount.  If you're overloading this you probably also want to overload getTotalAmount
}


export default duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(FeeListItemAmount);
